/**
 * Custom currency converter for the ACEHIGH theme.
 *
 * Display-only conversion of MYR prices on the storefront. Cart, checkout, and
 * order processing all stay in MYR — this component never mutates form values
 * or cart line item data.
 *
 * Hook contract (single source of truth):
 *   - Every price-bearing element on the page renders with the markup produced
 *     by `snippets/money-display.liquid`:
 *
 *       <span class="js-money" data-base-price-myr="2000">RM 20.00</span>
 *
 *   - `data-base-price-myr` carries the raw MYR price in *cents*. The visible
 *     text is the server-rendered MYR fallback used when the converter is
 *     inactive or rates fail to load.
 *   - The converter NEVER reads existing `textContent` as input. Every conversion
 *     pass reads cents from the data attribute and overwrites textContent. This
 *     is the rule that prevents double-conversion bugs after the active
 *     currency changes.
 *
 * This file is the core of the converter and intentionally limited in scope —
 *   - hardcoded rates / geolocation are stubbed and replaced in later steps
 *   - rate fetching, IP geolocation, mutation observation, debug helpers all
 *     land in later files
 *
 * @module currency-converter
 */

import { Component } from '@theme/component';
import { formatMoney } from '@theme/money-formatting';
import { getRates } from '@theme/currency-rates';
import { getDecidedCurrency, setStoredChoice } from '@theme/currency-geolocation';

/** Currency code of the store's base currency. All cents are denominated in this. */
const BASE_CURRENCY = 'MYR';

/**
 * Fallback currency formats. The real config is loaded from
 * `_currency-config.liquid`'s embedded JSON; this constant only ever applies
 * if the config script tag is missing (developer error / forgot to render
 * the snippet in `theme.liquid`).
 *
 * @type {Record<string, { format: string }>}
 */
const FALLBACK_FORMATS = {
  MYR: { format: 'RM {{amount}}' },
  USD: { format: '${{amount}} USD' },
  AUD: { format: '${{amount}} AUD' },
  SGD: { format: 'S${{amount}} SGD' },
  EUR: { format: '€{{amount}} EUR' },
  GBP: { format: '£{{amount}} GBP' },
};

/**
 * @typedef {Object} CurrencyConfig
 * @property {string} baseCurrency
 * @property {string[]} enabledCurrencies
 * @property {number} marginPct
 * @property {number} refreshHours
 * @property {string} rowDefault
 * @property {Record<string, number>} manualOverrides
 * @property {Record<string, { format: string }>} currencyFormats
 * @property {Record<string, string>} countryToCurrency
 * @property {string | null} shopifyHintCountry
 */

/**
 * @typedef {Object} CurrencyState
 * @property {string} active - ISO currency code currently displayed
 * @property {Record<string, number>} rates - rate per non-base currency, e.g. { USD: 0.21 }
 * @property {number} marginPct - 0–5; FX buffer added to rates (see #applyMargin)
 * @property {string} ratesSource - which leg of the fallback chain produced rates (see currency-rates.js)
 * @property {number} ratesFetchedAt - epoch ms of the last rate fetch
 */

/**
 * @typedef {Object} CurrencyConverterRefs
 * @property {HTMLElement} [debugSlot]
 */

/**
 * @extends {Component<CurrencyConverterRefs>}
 */
class CurrencyConverter extends Component {
  /** @type {CurrencyState} */
  #state = {
    active: BASE_CURRENCY,
    rates: {},
    marginPct: 0,
    ratesSource: 'empty',
    ratesFetchedAt: 0,
  };

  /** @type {CurrencyConfig | null} */
  #config = null;

  /** @type {MutationObserver | null} */
  #observer = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    this.#config = this.#readConfig();
    document.addEventListener('currency:change', this.#handleCurrencyChange);

    // Section Rendering API responses (cart drawer, variant updates, AJAX-loaded
    // collections, quick-view modals) all swap fresh server-rendered HTML into
    // the page. The server always renders MYR — without this observer, those
    // updates would flash MYR for a frame in the active currency's place. We
    // re-convert any new `.js-money` nodes the moment they enter the DOM.
    this.#observer = new MutationObserver(this.#handleMutations);
    this.#observer.observe(document.body, { childList: true, subtree: true });

    // ThemeEvents.cartUpdate is dispatched by Horizon's cart machinery after
    // cart-drawer / cart-page section morphs. Re-converting on this event is
    // a belt-and-braces backup in case the MutationObserver missed a node
    // (e.g. text-only changes inside an existing .js-money span).
    document.addEventListener('cart:update', this.#handleAjaxUpdate, {
      signal: this.#abortController.signal,
    });
    document.addEventListener('variant:update', this.#handleAjaxUpdate, {
      signal: this.#abortController.signal,
    });

    this.#boot();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('currency:change', this.#handleCurrencyChange);
    this.#observer?.disconnect();
    this.#observer = null;
    this.#abortController.abort();
  }

  /**
   * @param {MutationRecord[]} mutations
   */
  #handleMutations = (mutations) => {
    if (this.#state.active === BASE_CURRENCY && Object.keys(this.#state.rates).length === 0) {
      // Converter is in MYR and has no rates — server-rendered text is
      // already correct. Skip the work.
      return;
    }

    for (const m of mutations) {
      for (const node of m.addedNodes) {
        if (!(node instanceof Element)) continue;

        // Convert the node itself if it's a price hook…
        if (node.matches?.('.js-money[data-base-price-myr]')) {
          this.#convertElement(/** @type {HTMLElement} */ (node));
        }

        // …and any price hooks underneath it.
        const inner = node.querySelectorAll?.('.js-money[data-base-price-myr]');
        if (inner && inner.length) {
          for (const el of inner) this.#convertElement(/** @type {HTMLElement} */ (el));
        }
      }
    }
  };

  /**
   * Backup re-conversion triggered by Horizon's cart/variant events.
   *
   * Critical timing detail: in `component-cart-items.js` the `cart:update`
   * event is dispatched BEFORE `morphSection()` runs, so a synchronous
   * `#convertAll()` would query the stale DOM (existing line items already
   * in the active currency) and miss the new line item / updated total that
   * morph is about to swap in. We defer one animation frame so morph has
   * committed its DOM mutations before we re-convert.
   *
   * The MutationObserver catches *new* nodes morph adds (e.g. a brand-new
   * cart line `<tr>`), but morph also updates `textContent` in place on
   * existing `.js-money` spans (e.g. the cart total inside `<text-component>`)
   * — those are characterData mutations that childList:subtree doesn't fire
   * for. The rAF-deferred `#convertAll()` is what catches those.
   */
  #handleAjaxUpdate = () => {
    requestAnimationFrame(() => this.#convertAll());
  };

  // ---------------------------------------------------------------------------
  // Boot
  // ---------------------------------------------------------------------------

  /**
   * Reads the JSON config emitted by `_currency-config.liquid`. Returns a
   * minimal fallback if missing — converter will degrade to MYR display.
   * @returns {CurrencyConfig}
   */
  #readConfig() {
    const tag = document.getElementById('ace-currency-config');
    if (!tag || !tag.textContent) {
      console.warn('[currency-converter] config tag missing; converter inactive');
      return {
        baseCurrency: BASE_CURRENCY,
        enabledCurrencies: ['MYR'],
        marginPct: 0,
        refreshHours: 12,
        rowDefault: 'MYR',
        manualOverrides: {},
        currencyFormats: FALLBACK_FORMATS,
        countryToCurrency: {},
        shopifyHintCountry: null,
      };
    }
    try {
      return JSON.parse(tag.textContent);
    } catch (err) {
      console.error('[currency-converter] config JSON parse failed:', err);
      return {
        baseCurrency: BASE_CURRENCY,
        enabledCurrencies: ['MYR'],
        marginPct: 0,
        refreshHours: 12,
        rowDefault: 'MYR',
        manualOverrides: {},
        currencyFormats: FALLBACK_FORMATS,
        countryToCurrency: {},
        shopifyHintCountry: null,
      };
    }
  }

  /**
   * Boot sequence:
   *   1. Decide which currency to show (manual choice → IP detect → ROW default).
   *   2. Fetch rates (cache → primary API → failover → stale cache → empty).
   *   3. Convert all visible prices.
   * Steps 1 and 2 run in parallel — they're independent.
   *
   * FOUC handling:
   *   The body gets `data-currency-loading` set as early as the script can
   *   reach (see boot block in this file), which the stylesheet uses to hide
   *   the contents of `.js-money` spans. Once the first `#convertAll()` pass
   *   completes we remove the attribute and the prices fade in. If the boot
   *   path errors, the attribute is removed in the catch block so prices
   *   never stay invisible.
   */
  async #boot() {
    if (!this.#config) {
      this.#clearLoadingState();
      return;
    }
    const cfg = this.#config;

    try {
      const [decidedCurrency, rateBundle] = await Promise.all([
        getDecidedCurrency({
          countryToCurrency: cfg.countryToCurrency,
          rowDefault: cfg.rowDefault,
          enabledCurrencies: cfg.enabledCurrencies,
          shopifyHintCountry: cfg.shopifyHintCountry,
        }),
        getRates({
          enabledCurrencies: cfg.enabledCurrencies,
          manualOverrides: cfg.manualOverrides,
          ttlMs: cfg.refreshHours * 60 * 60 * 1000,
        }),
      ]);

      this.#state.rates = rateBundle.rates;
      this.#state.marginPct = cfg.marginPct;
      this.#state.ratesSource = rateBundle.source;
      this.#state.ratesFetchedAt = rateBundle.fetchedAt;
      this.#state.active = decidedCurrency;

      this.#convertAll();
      this.#installDebugHelper();

      console.info(
        `[currency-converter] active=${decidedCurrency} source=${rateBundle.source}` +
          ` cache_age=${this.#cacheAgeLabel()} margin=${cfg.marginPct}%`
      );
      document.dispatchEvent(new CustomEvent('currency:ready', { detail: this.getState() }));
    } catch (err) {
      console.error('[currency-converter] boot failed:', err);
    } finally {
      this.#clearLoadingState();
    }
  }

  /**
   * Removes the FOUC-suppression attribute set by the inline script in
   * `_currency-config.liquid`. Idempotent and safe to call multiple times
   * (paths: boot success, boot error, missing config).
   */
  #clearLoadingState() {
    document.documentElement.removeAttribute('data-currency-loading');
  }

  /**
   * Installs `window.__aceCurrency.debug()` so you can verify in production
   * what the converter is actually doing without opening the network tab.
   *
   * Usage in console:
   *   window.__aceCurrency.debug()
   *   → { active: 'USD', source: 'primary', fetchedAt: '2 minutes ago', rates: {...}, margin: '1.5%' }
   *
   * No PII is logged. Helper is silently absent if there's no config.
   */
  #installDebugHelper() {
    /** @type {any} */
    const w = window;
    w.__aceCurrency ??= {};
    w.__aceCurrency.debug = () => {
      const state = this.getState();
      return {
        active: state.active,
        source: state.ratesSource,
        fetchedAt: state.ratesFetchedAt
          ? new Date(state.ratesFetchedAt).toISOString()
          : 'n/a (manual or empty)',
        cacheAge: this.#cacheAgeLabel(),
        rates: state.rates,
        margin: `${state.marginPct}%`,
      };
    };
    w.__aceCurrency.setCurrency = (/** @type {string} */ currency) => {
      this.setActiveCurrency(currency);
    };
  }

  /** @returns {string} */
  #cacheAgeLabel() {
    const ts = this.#state.ratesFetchedAt;
    if (!ts) return 'n/a';
    const ageMs = Date.now() - ts;
    const mins = Math.round(ageMs / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins} min ago`;
    const hours = Math.round(mins / 60);
    return `${hours} h ago`;
  }

  // ---------------------------------------------------------------------------
  // State accessors (used by the public debug helper in step 8)
  // ---------------------------------------------------------------------------

  getState() {
    // Defensive copy so callers can't mutate internal state.
    return {
      active: this.#state.active,
      rates: { ...this.#state.rates },
      marginPct: this.#state.marginPct,
      ratesSource: this.#state.ratesSource,
      ratesFetchedAt: this.#state.ratesFetchedAt,
    };
  }

  /**
   * Switches the active display currency and triggers a re-conversion pass.
   * Persists the choice so it survives across sessions and overrides auto-detection.
   * @param {string} currency
   * @param {{ persist?: boolean }} [options]
   */
  setActiveCurrency(currency, { persist = true } = {}) {
    if (currency === this.#state.active) return;
    const formats = this.#config?.currencyFormats ?? FALLBACK_FORMATS;
    if (currency !== BASE_CURRENCY && !formats[currency]) {
      console.warn(`[currency-converter] Unknown currency "${currency}", staying on ${this.#state.active}`);
      return;
    }
    this.#state.active = currency;
    if (persist) setStoredChoice(currency);
    this.#convertAll();
    document.dispatchEvent(new CustomEvent('currency:applied', { detail: this.getState() }));
  }

  // ---------------------------------------------------------------------------
  // Conversion
  // ---------------------------------------------------------------------------

  /**
   * Walk every `js-money` span in the document and update its visible text to
   * match the active currency. ALWAYS reads cents from `data-base-price-myr`
   * — never from current textContent — to avoid double-conversion drift after
   * a currency switch.
   *
   * @param {ParentNode} [root] - Optional subtree to limit conversion to. Used
   *   by the MutationObserver in step 7 to convert just-added nodes.
   */
  #convertAll(root = document) {
    const elements = root.querySelectorAll('.js-money[data-base-price-myr]');
    for (const el of elements) {
      this.#convertElement(/** @type {HTMLElement} */ (el));
    }
  }

  /**
   * Converts a single `js-money` element in place.
   * @param {HTMLElement} el
   */
  #convertElement(el) {
    const cents = parseInt(el.dataset.basePriceMyr ?? '', 10);
    if (!Number.isFinite(cents) || cents === 0) return;

    const target = this.#state.active;
    const formats = this.#config?.currencyFormats ?? FALLBACK_FORMATS;

    // MYR display: re-format using the MYR format string (which uses
    // `{{amount_no_decimals}}`, so the rendered text drops the .00).
    if (target === BASE_CURRENCY) {
      const fmt = formats[BASE_CURRENCY]?.format ?? '{{amount_no_decimals}}';
      el.textContent = formatMoney(cents, fmt, BASE_CURRENCY);
      this.#syncTextComponentValue(el);
      return;
    }

    const rate = this.#state.rates[target];
    if (!Number.isFinite(rate) || rate <= 0) {
      // No rate available — keep MYR fallback (server-rendered text is already MYR).
      return;
    }

    const adjustedRate = this.#applyMargin(rate);

    // Non-MYR display: ALWAYS round UP to a whole unit (dollar/pound/euro)
    // before formatting. Two reasons:
    //   1. Foreign-currency prices look cleaner without trailing fractional
    //      cents (no $108.62 — just $109.00).
    //   2. Math.ceil (never round, never floor) ensures the displayed price
    //      is never less than the real converted value, so the customer is
    //      never quoted a price lower than what their cart will actually
    //      cost in MYR-equivalent. Combined with the FX margin buffer, this
    //      gives a small but consistent overage in our favor.
    const rawTargetCents = cents * adjustedRate;
    const wholeUnits = Math.ceil(rawTargetCents / 100);
    const targetCents = wholeUnits * 100;

    const fmt = formats[target]?.format;
    if (!fmt) return;

    el.textContent = formatMoney(targetCents, fmt, target);
    this.#syncTextComponentValue(el);
  }

  /**
   * If the converted span sits inside a Horizon `<text-component>`, mirror the
   * new visible text into the parent's `value=` attribute so screen-reader
   * shimmer announcements stay consistent with what's on screen. Best-effort —
   * silently no-ops when not inside a text-component.
   *
   * @param {HTMLElement} el
   */
  #syncTextComponentValue(el) {
    const tc = el.closest('text-component');
    if (!tc) return;
    // Reconstruct the value from the full text-component subtree so adjacent
    // text nodes (e.g. discount labels) stay correct.
    tc.setAttribute('value', tc.textContent?.trim() ?? '');
  }

  /**
   * Applies the FX margin buffer to a raw rate.
   *
   * Why a buffer:
   *   FX rates drift between fetches. By inflating the displayed price by a
   *   small percentage, we protect against the case where MYR strengthens
   *   between rate refreshes — without the buffer, a customer paying $X today
   *   could effectively be charged less than the displayed equivalent if the
   *   real rate moved against us before the next refresh.
   *
   *   margin of 1.5% means: displayed foreign price is 1.5% higher than the
   *   raw rate would imply. For a customer in USD seeing "$20.00", the actual
   *   MYR charge at checkout corresponds to roughly $20.00 / 1.015 ≈ $19.70
   *   in real-world rate terms. The customer always pays MYR; the buffer
   *   exists so the merchant never under-charges due to rate drift.
   *
   * @param {number} rate - Raw rate (target currency per 1 MYR)
   * @returns {number} Rate with buffer applied
   */
  #applyMargin(rate) {
    const pct = Math.max(0, Math.min(5, this.#state.marginPct));
    return rate * (1 + pct / 100);
  }

  // ---------------------------------------------------------------------------
  // Event handlers
  // ---------------------------------------------------------------------------

  /**
   * Fired by `<currency-selector>` when the visitor picks a currency.
   * @param {Event} event
   */
  #handleCurrencyChange = (event) => {
    const detail = /** @type {CustomEvent} */ (event).detail;
    if (!detail || typeof detail.currency !== 'string') return;
    this.setActiveCurrency(detail.currency);
  };
}

if (!customElements.get('currency-converter')) {
  customElements.define('currency-converter', CurrencyConverter);
}

export { CurrencyConverter, BASE_CURRENCY };
